import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { Button, Box, Chip } from "@mui/material";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";

// Components
import { useNotification } from "../utils/Notification";
import Message from "../modals/Message";
import ConfirmationModal from "../utils/ConfirmationModal";
import {
  formatDate,
  getLocalTimestamp,
  generateColorForChip,
  trackEvent,
} from "../utils/Utils";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";
import { useTranslation } from "react-i18next";

export default function Upcoming({ runTutorial }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessageModalOpen, setNewMessageModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showResumeButton, setShowResumeButton] = useState(false);
  const [showPauseButton, setShowPauseButton] = useState(false);

  // Context
  const { showNotification } = useNotification();

  const typeMapping = {
    me: t("upcoming.me"),
    group: t("upcoming.group"),
    bulk: t("upcoming.bulk"),
    contact: t("upcoming.contact"),
    manual: t("upcoming.manual"),
  };

  const frequencyMapping = {
    once: t("upcoming.once"),
    daily: t("upcoming.daily"),
    weekly: t("upcoming.weekly"),
    biweekly: t("upcoming.biweekly"),
    monthly: t("upcoming.monthly"),
    bimester: t("upcoming.bimester"),
    trimester: t("upcoming.trimester"),
    quarter: t("upcoming.quarter"),
    semester: t("upcoming.semester"),
    yearly: t("upcoming.yearly"),
  };

  const messageStatusMapping = {
    active: t("upcoming.active"),
    processing: t("upcoming.processing"),
    paused: t("upcoming.paused"),
  };

  const columns = [
    {
      field: "type",
      headerName: t("upcoming.type"),
      width: 110,
      valueGetter: (params) => typeMapping[params.row.type] || params.row.type,
    },
    { field: "to", headerName: t("upcoming.to"), width: 200 },
    { field: "message", headerName: t("upcoming.message"), width: 300 },
    { field: "file", headerName: t("upcoming.file"), width: 200 },
    {
      field: "label",
      headerName: t("upcoming.label"),
      width: 150,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }

        const labelColor = generateColorForChip(params.value);
        return (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            sx={{
              borderColor: labelColor,
              color: labelColor,
              height: "20px",
            }}
          />
        );
      },
    },
    {
      field: "frequency",
      headerName: t("upcoming.frequency"),
      width: 100,
      valueGetter: (params) =>
        frequencyMapping[params.row.frequency] || params.row.frequency,
    },
    {
      field: "message_status",
      headerName: t("upcoming.status"),
      width: 120,
      renderCell: (params) => {
        const status = params.row.message_status;
        let chipColor =
          status === "active"
            ? "green"
            : status === "paused"
              ? "red"
              : "darkOrange";

        return (
          <Chip
            label={
              messageStatusMapping[params.row.message_status] ||
              params.row.message_status
            }
            color="primary"
            variant="outlined"
            sx={{
              borderColor: chipColor,
              color: chipColor,
              height: "20px",
            }}
          />
        );
      },
    },
    {
      field: "next_at",
      headerName: t("upcoming.nextAt"),
      width: 180,
      valueGetter: (params) =>
        params.row.next_at ? formatDate(params.row.next_at) : "",
    },
    {
      field: "last_at",
      headerName: t("upcoming.lastAt"),
      width: 180,
      valueGetter: (params) =>
        params.row.last_at ? formatDate(params.row.last_at) : "",
    },
    {
      field: "created_at",
      headerName: t("upcoming.createdAt"),
      width: 180,
      valueGetter: (params) =>
        params.row.created_at ? formatDate(params.row.created_at) : "",
    },
  ];

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("messages")
        .select(
          `
              id,
              to,
              contact_id (
                id,
                first_name,
                last_name,
                nickname
              ),
              group_id (
                id,
                name
              ),
              bulk_contacts,
              name,
              countrycode_id,
              phone,
              message,
              file_id (
                id,
                name
              ),
              label_id (
                id,
                name
              ),
              message_frequency,
              message_status,
              scheduled_timestamp,
              end_timestamp,
              created_at
            `
        )
        .in("message_status", ["active", "processing", "paused"]);

      if (error) {
        showNotification(
          `${t("notification.errorFetchingMessages")}: ${error.message}`,
          "error"
        );
      } else {
        const formattedMessages = data.map((message) => ({
          id: message.id,
          type: message.to,
          to:
            message.to === "contact"
              ? `${message.contact_id.first_name} ${message.contact_id.last_name
              }${message.contact_id.nickname
                ? ` (${message.contact_id.nickname})`
                : ""
              }`
              : message.to === "group"
                ? message.group_id.name
                : message.to === "me"
                  ? t("upcoming.myself")
                  : message.name,
          message: message.message,
          file: message.file_id?.name,
          label: message.label_id?.name,
          frequency: message.message_frequency,
          message_status: message.message_status,
          next_at: getLocalTimestamp(message.scheduled_timestamp),
          last_at: getLocalTimestamp(message.end_timestamp),
          contact_id: message.contact_id?.id,
          group_id: message.group_id?.id,
          bulk_contacts: message.bulk_contacts,
          name: message.name,
          countrycode_id: message.countrycode_id,
          phone: message.phone,
          file_id: message.file_id?.id,
          label_id: message.label_id?.id,
          created_at: getLocalTimestamp(message.created_at),
        }));

        // Sorting is all paused messages at the bottom, and then oldest `next_at` at the top
        formattedMessages.sort((a, b) => {
          // First, check if one message is paused and the other is not
          if (a.message_status === "paused" && b.message_status !== "paused")
            return 1;
          if (a.message_status !== "paused" && b.message_status === "paused")
            return -1;

          // If both are either paused or not paused, sort by `next_at`
          return new Date(a.next_at) - new Date(b.next_at);
        });

        setMessages(formattedMessages);
      }
    };

    fetchMessages();
  }, [newMessageModalOpen, showDeleteConfirmation]);

  // Control modal and notify parent when it's ready
  useEffect(() => {
    if (runTutorial) {
      setNewMessageModalOpen(true);
    }
  }, [runTutorial]);

  const handleNewMessages = () => {
    trackEvent("upcoming-new_message_clicked");
    setNewMessageModalOpen(true);
  };

  const handleCloseNewMessagesModal = () => {
    trackEvent("upcoming-message_modal_closed");
    setSelectedMessage(null);
    setNewMessageModalOpen(false);
  };

  const handleRowClick = (row) => {
    trackEvent("upcoming-row_clicked");
    setSelectedMessage(row);
    setNewMessageModalOpen(true);
  };

  const handleRowSelection = (selection) => {
    trackEvent("upcoming-message_selected");
    setSelectedRows(selection);

    if (selection.length > 0) {
      const selectedMessages = messages.filter((message) =>
        selection.includes(message.id)
      );

      const allPaused = selectedMessages.every(
        (message) => message.message_status === "paused"
      );
      setShowResumeButton(allPaused);

      const allActive = selectedMessages.every(
        (message) =>
          message.message_status === "active" ||
          message.message_status === "processing"
      );
      setShowPauseButton(allActive);
    } else {
      setShowPauseButton(false);
      setShowResumeButton(false);
    }
  };

  const handleDeleteConfirmation = () => {
    trackEvent("upcoming-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancellation = () => {
    trackEvent("upcoming-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  const handleDelete = async () => {
    trackEvent("upcoming-delete_confirmed");
    try {
      const { error } = await supabase
        .from("messages")
        .delete()
        .in("id", selectedRows);

      if (error) {
        throw error;
      }

      setShowDeleteConfirmation(false);
      setShowPauseButton(false);
      setShowResumeButton(false);
      setSelectedRows([]);
    } catch (error) {
      showNotification(
        `${t("notification.errorDeletingMessages")}: ${error.message}`,
        "error"
      );
    }
  };

  const updateMessageStatus = async (status) => {
    trackEvent("upcoming-update_message_status", { status });
    try {
      const { error } = await supabase
        .from("messages")
        .update({ message_status: status })
        .in("id", selectedRows);

      if (error) {
        throw error;
      }

      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          selectedRows.includes(message.id)
            ? { ...message, message_status: status }
            : message
        )
      );

      setShowPauseButton(false);
      setShowResumeButton(false);
      setSelectedRows([]);
    } catch (error) {
      showNotification(
        `${t("notification.errorUpdatingMessages")}: ${error.message}`,
        "error"
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          rows={messages}
          columns={columns}
          checkboxSelection
          disableSelectionOnClick
          onRowClick={(params) => handleRowClick(params.row)}
          onSelectionModelChange={handleRowSelection}
          rowSelectionModel={selectedRows}
          autoHeight
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewMessages}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {t("upcoming.newMessage")}
                </Button>

                {selectedRows.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteOutlinedIcon />}
                    color="error"
                    onClick={handleDeleteConfirmation}
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" } }}
                  >
                    {t("upcoming.delete")}
                  </Button>
                )}

                {showResumeButton && (
                  <Button
                    variant="outlined"
                    startIcon={<PlayCircleOutlinedIcon />}
                    color="warning"
                    onClick={() => updateMessageStatus("active")}
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#4cceac", borderColor: "#4cceac" }}
                  >
                    {t("upcoming.resume")}
                  </Button>
                )}

                {showPauseButton && (
                  <Button
                    variant="outlined"
                    startIcon={<PauseCircleOutlinedIcon />}
                    color="warning"
                    onClick={() => updateMessageStatus("paused")}
                    sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#e2726e", borderColor: "#e2726e" }}
                  >
                    {t("upcoming.pause")}
                  </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <Message
        open={newMessageModalOpen}
        onClose={handleCloseNewMessagesModal}
        message={selectedMessage}
        showButton={true}
        isJoyrideRunning={runTutorial}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={handleDeleteCancellation}
        onConfirm={handleDelete}
        title={t("upcoming.confirmDelete")}
        message={t("upcoming.confirmDeleteMessage")}
      />
    </Box>
  );
}
